import { useState } from "react"
import { Drawer, Button, List, Input, Space, Popconfirm, message, Typography } from "antd"
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  SaveOutlined,
  CloseOutlined,
  SettingOutlined,
} from "@ant-design/icons"
import { useCollection } from "../../../hooks/useCollection" // Assuming path is correct
import { db } from "../../../firebase/config" // Assuming path is correct
import { collection, addDoc, doc, updateDoc, deleteDoc } from "firebase/firestore"

const { Text } = Typography

export default function PalletLocationsDrawer() {
  // --- Component State ---
  const [open, setOpen] = useState(false)
  const [newLocation, setNewLocation] = useState("")
  const [editingKey, setEditingKey] = useState("")
  const [editingName, setEditingName] = useState("")
  const [loading, setLoading] = useState(false)

  // --- Data Fetching ---
  const { data: palletLocations, loading: locationsLoading } = useCollection("palletLocations")

  // --- Drawer Visibility Handlers ---
  const showDrawer = () => setOpen(true)
  const onClose = () => {
    setOpen(false)
    setEditingKey("") // Reset editing state when closing
  }

  // --- CRUD Handlers ---

  /**
   * Adds a new pallet location to Firestore.
   */
  const handleAddLocation = async () => {
    if (!newLocation.trim()) {
      message.warning("Location name cannot be empty.")
      return
    }
    setLoading(true)
    try {
      await addDoc(collection(db, "palletLocations"), {
        name: newLocation.trim(),
        // You can add more fields here if needed, e.g., createdBy, createdAt
      })
      message.success(`Location "${newLocation.trim()}" added successfully.`)
      setNewLocation("") // Clear input after successful add
    } catch (error) {
      console.error("Error adding location: ", error)
      message.error("Failed to add location. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  /**
   * Deletes a pallet location from Firestore.
   * @param {string} id - The ID of the document to delete.
   */
  const handleDeleteLocation = async (id) => {
    try {
      await deleteDoc(doc(db, "palletLocations", id))
      message.success("Location deleted successfully.")
    } catch (error) {
      console.error("Error deleting location: ", error)
      message.error("Failed to delete location. Please try again.")
    }
  }

  /**
   * Updates the name of a pallet location in Firestore.
   * @param {string} id - The ID of the document to update.
   */
  const handleUpdateLocation = async (id) => {
    if (!editingName.trim()) {
      message.warning("Location name cannot be empty.")
      return
    }
    try {
      const locationRef = doc(db, "palletLocations", id)
      await updateDoc(locationRef, { name: editingName.trim() })
      message.success("Location updated successfully.")
      setEditingKey("") // Exit editing mode
    } catch (error) {
      console.error("Error updating location: ", error)
      message.error("Failed to update location. Please try again.")
    }
  }

  // --- Edit Mode Handlers ---

  /**
   * Enters editing mode for a specific list item.
   * @param {object} item - The location item to edit.
   */
  const handleEdit = (item) => {
    setEditingKey(item.id)
    setEditingName(item.name)
  }

  /**
   * Cancels editing mode.
   */
  const cancelEdit = () => {
    setEditingKey("")
  }

  return (
    <>
      <Button icon={<SettingOutlined />} onClick={showDrawer}>
        Manage Locations
      </Button>
      <Drawer
        title="Manage Pallet Locations"
        placement="right"
        onClose={onClose}
        open={open}
        width={400}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {/* --- Add New Location Form --- */}
          <Text>Add New Location</Text>
          <Space.Compact style={{ width: "100%" }}>
            <Input
              placeholder="PA17.."
              value={newLocation}
              onChange={(e) => setNewLocation(e.target.value)}
              onPressEnter={handleAddLocation}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddLocation}
              loading={loading}>
              Add
            </Button>
          </Space.Compact>

          {/* --- List of Existing Locations --- */}
          <List
            header={<Text strong>Existing Locations</Text>}
            bordered
            loading={locationsLoading}
            dataSource={[...(palletLocations || [])].sort((a, b) => {
              const getNum = (name) => {
                const match = name.match(/\d+$/) // take trailing digits
                return match ? parseInt(match[0], 10) : 0
              }
              return getNum(b.name) - getNum(a.name) // descending
            })}
            renderItem={(item) => {
              const isEditing = item.id === editingKey
              return (
                <List.Item>
                  {isEditing ? (
                    // --- Editing View ---
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onPressEnter={() => handleUpdateLocation(item.id)}
                      />
                      <Button
                        type="primary"
                        icon={<SaveOutlined />}
                        onClick={() => handleUpdateLocation(item.id)}
                      />
                      <Button icon={<CloseOutlined />} onClick={cancelEdit} />
                    </Space.Compact>
                  ) : (
                    // --- Default View ---
                    <Space
                      align="center"
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}>
                      <Text>{item.name}</Text>
                      <Space>
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => handleEdit(item)}
                        />
                        <Popconfirm
                          title="Delete this location?"
                          description="Are you sure? This cannot be undone."
                          onConfirm={() => handleDeleteLocation(item.id)}
                          okText="Yes, Delete"
                          cancelText="No">
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      </Space>
                    </Space>
                  )}
                </List.Item>
              )
            }}
          />
        </Space>
      </Drawer>
    </>
  )
}
